import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import get from 'lodash/get';
import { connect } from 'react-redux';

import Cart from 'bento-ordering/cart/components/Cart';
import { isMobileMedia } from 'bento-ordering/utils/media';
import { generatePath } from 'bento-ordering/utils/generatePath';
import { clearCart, removeCartItem } from 'bento-ordering/cart/actions/index';

import { CART } from 'bento-ordering/cart/constants/entities';
import ConfirmContainer from 'bento-ordering/base/containers/ConfirmContainer';
import CartConfirmModal from 'bento-ordering/cart/components/CartConfirmModal';
import {
  isSubtotalBelowMinimumSelector,
  getFulfillmentTimeOrASAPDisplaySelector
} from 'bento-ordering/cart/selectors';
import { getCartMinimumSelector } from 'bento-ordering/account/reducers';

import withLoading from 'bento-ordering/base/hoc/withLoading';
import LaunchNotificationOnMount from 'bento-ordering/base/containers/LaunchNotificationOnMount';
import SuccessfulEditSnackbar from 'bento-ordering/base/components/SuccessfulEditSnackbar';
import { restartOnlineOrderingFlow } from 'bento-ordering/base/actions/setupInitialData';
import { resetLastItem } from 'bento-ordering/cart/actions/lastItem';
import { getItemByIdSelector } from 'bento-ordering/base/reducers/bentoApi';
import {
  goToMenu,
  goToOrderSettings
} from 'bento-ordering/cart/actions/orderSettings';

import validateCart from 'bento-ordering/cart/actions/validateCart';
import isDineInApplicationVariant from 'bento-ordering/utils/isDineInApplicationVariant';

class CartContainer extends React.Component {
  static propTypes = {
    cart: PropTypes.object,
    history: PropTypes.shape({ push: PropTypes.func, goBack: PropTypes.func }),
    dispatch: PropTypes.func,
    currentPrepTime: PropTypes.string,
    locationDetail: PropTypes.object,
    restaurantName: PropTypes.string,
    getIsExpired: PropTypes.func,
    location: PropTypes.object,
    lastItem: PropTypes.string,
    match: PropTypes.shape({
      url: PropTypes.string
    }),
    selectedLocationSlug: PropTypes.id,
    isTotalBelowMinimum: PropTypes.bool,
    orderMinimum: PropTypes.number,
    fulfillmentAddress: PropTypes.string,
    fulfillmentTimeOrASAP: PropTypes.string,
    fulfillmentMethod: PropTypes.string,
    isDeliveryWithNoAddress: PropTypes.bool,
    deliveryFee: PropTypes.string,
    customFee: PropTypes.string,
    customFeeName: PropTypes.string,
    bundleServiceFeeWithCustomFee: PropTypes.bool,
    bundledServiceAndCustomFees: PropTypes.string
  };

  constructor(props) {
    super(props);

    this.cartRef = React.createRef();
    this.isMobile = isMobileMedia();
  }

  componentDidMount() {
    const { dispatch } = this.props;
    if (this.isCartEmpty()) {
      this.handleClose();
    } else {
      dispatch(validateCart());
    }
  }

  isCartEmpty() {
    return get(this.props.cart, 'items', []).length === 0;
  }

  componentDidUpdate(prevProps) {
    if (get(prevProps.cart, 'items', []).length !== 0 && this.isCartEmpty()) {
      this.clearCart();
    }
  }

  goToOrderSettings = () => {
    this.props.dispatch(goToOrderSettings());
  };

  handleClose = () => {
    this.navigateToMenu();
  };

  navigateToMenu = () => {
    this.props.dispatch(goToMenu());
  };

  clearCart = () => {
    this.props.dispatch(clearCart());
    this.handleClose();
  };

  getEditCartItemPath = cartItemId => {
    return generatePath(CART, 'cartItem', {
      cartItemId
    });
  };

  removeItemFromCart = async id => {
    const { dispatch } = this.props;
    const result = await dispatch(removeCartItem(id));

    if (result.payload.status === 404) {
      dispatch(restartOnlineOrderingFlow());
    } else if (get(result, 'payload.items', []).length === 0) {
      this.handleClose();
    }
  };

  render() {
    const {
      cart,
      restaurantName,
      currentPrepTime,
      match,
      lastItem,
      dispatch,
      isTotalBelowMinimum,
      orderMinimum,
      fulfillmentAddress,
      fulfillmentTimeOrASAP,
      fulfillmentMethod,
      deliveryFee,
      customFee,
      customFeeName,
      bundleServiceFeeWithCustomFee,
      bundledServiceAndCustomFees
    } = this.props;

    const isDineInVariant = isDineInApplicationVariant();

    return (
      <div>
        <LaunchNotificationOnMount handleCallback={this.handleClose} />
        <ConfirmContainer>
          {(isOpen, toggleIsOpen) => (
            <React.Fragment>
              <Cart
                {...cart}
                ref={this.cartRef}
                restaurantName={restaurantName}
                handleClose={this.handleClose}
                handleEditClick={this.handleEditClick}
                currentPrepTime={currentPrepTime}
                clearCart={toggleIsOpen}
                getEditCartItemPath={this.getEditCartItemPath}
                removeItemFromCart={this.removeItemFromCart}
                currentCartPath={match.url}
                changeFulfillment={
                  // Order settings is not available for dine-in
                  isDineInVariant ? null : this.goToOrderSettings
                }
                isTotalBelowMinimum={isTotalBelowMinimum}
                orderMinimum={orderMinimum}
                fulfillmentAddress={fulfillmentAddress}
                fulfillmentTimeOrASAP={fulfillmentTimeOrASAP}
                fulfillmentMethod={fulfillmentMethod}
                deliveryFee={deliveryFee}
                customFee={customFee}
                customFeeName={customFeeName}
                bundleServiceFeeWithCustomFee={bundleServiceFeeWithCustomFee}
                bundledServiceAndCustomFees={bundledServiceAndCustomFees}
              />
              <CartConfirmModal
                isOpen={isOpen}
                toggleIsOpen={toggleIsOpen}
                handleConfirm={this.clearCart}
                container={this.cartRef.current}
              />
            </React.Fragment>
          )}
        </ConfirmContainer>
        <SuccessfulEditSnackbar
          open={!!lastItem}
          item={lastItem}
          onClose={() => dispatch(resetLastItem())}
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { cart, restaurantLocations, lastItem, locationThrottledTimes } = state;
  const selectedLocation = getItemByIdSelector(
    state.restaurantLocations,
    cart.online_ordering_location
  );

  const {
    online_ordering_fulfillment_method,
    online_ordering_delivery_fee,
    shipping_street_address_1,
    online_ordering_custom_fee,
    online_ordering_custom_fee_name,
    bundle_service_fee_with_custom_fee,
    bundled_service_and_custom_fees
  } = cart;
  const orderMinimum = getCartMinimumSelector(cart);
  const isTotalBelowMinimum = isSubtotalBelowMinimumSelector(cart);
  const fulfillmentTimeOrASAP = getFulfillmentTimeOrASAPDisplaySelector({
    restaurantLocations,
    cart,
    locationThrottledTimes
  });

  return {
    cart,
    restaurantName: get(selectedLocation, 'location.name'),
    selectedLocationSlug: get(selectedLocation, 'location.slug'),
    currentPrepTime: selectedLocation.current_prep_time,
    lastItem: lastItem.edit,
    isTotalBelowMinimum,
    orderMinimum,
    fulfillmentTimeOrASAP,
    /**
     * 12/20/2019 TODO: shipping_street_address_1 always gets set base on
     * the restaurant address which is a logic flaw because the user
     * will always have a delivery address even if it may be the
     * restaurant address. ECOM-980 will handle this fix and this
     * comment can be removed.
     */
    fulfillmentAddress: shipping_street_address_1,
    fulfillmentMethod: online_ordering_fulfillment_method,
    deliveryFee: online_ordering_delivery_fee,
    customFee: online_ordering_custom_fee,
    customFeeName: online_ordering_custom_fee_name,
    bundleServiceFeeWithCustomFee: bundle_service_fee_with_custom_fee,
    bundledServiceAndCustomFees: bundled_service_and_custom_fees
  };
};

export default withRouter(
  connect(mapStateToProps)(withLoading(CartContainer, CART))
);
